import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getOpenModalType, getIsModalOpen, getOpenModalConfig } from 'a/hub/selectors/modalSystemSelectors';
import { closeModal } from 'a/hub/actions/modalSystemActions';
import AnimatedModal from 'a/components/ModalSystem/AnimatedModal';
import { MODAL_TYPES } from 'a/components/ModalSystem';
import { getDevDirectGameId } from 'lib/utilityFunctions';

import LoginModal from './auth/LoginModal';
import TFELaunchModal from './TFE/TFELaunchModal';
import TFESuccessModal from './TFE/TFESuccessModal';
import TFEEarlyExitModal from './TFE/TFEEarlyExitModal';
import VocabLaunchModal from './vocab/VocabLaunchModal';
import VocabSuccessModal from './vocab/VocabSuccessModal';
import VocabEarlyExitModal from './vocab/VocabEarlyExitModal';
import EreaderLaunchModal from './eReader/EreaderLaunchModal';
import EreaderSuccessModal from './eReader/EreaderSuccessModal';
import EreaderEarlyExitModal from './eReader/EreaderEarlyExitModal';
import GameSuccessModal from './game/GameSuccessModal';
import GameFailureModal from './game/GameFailureModal';
import GameEarlyExitModal from './game/GameEarlyExitModal';
import NotificationModal from './informational/NotificationModal';
import EndOfAllQuestsAndChallengesModal from './quest/EndOfAllQuestsAndChallengesModal';
import GameSelectorModal from './game/GameSelectorModal';
import FluencyLaunchModal from './fluency/FluencyLaunchModal';
import ChallengeQuestSuccessModal from './challenge/ChallengeQuestSuccessModal';
import ChallengeQuestModal from './challenge/ChallengeQuestModal';
import EndOfQuestsModal from './quest/EndOfQuestsModal';
import RePlacementModal from './quest/RePlacementModal';
import LevelSummaryModal from './informational/LevelSummaryModal';
import QuestCompleteModal from './quest/QuestCompleteModal';

const MODAL_COMPONENTS = {
  [MODAL_TYPES.LOGIN]: LoginModal,
  [MODAL_TYPES.TFE_LAUNCH]: TFELaunchModal,
  [MODAL_TYPES.TFE_SUCCESS]: TFESuccessModal,
  [MODAL_TYPES.TFE_EARLY_EXIT]: TFEEarlyExitModal,
  [MODAL_TYPES.VOCAB_LAUNCH]: VocabLaunchModal,
  [MODAL_TYPES.VOCAB_SUCCESS]: VocabSuccessModal,
  [MODAL_TYPES.VOCAB_EARLY_EXIT]: VocabEarlyExitModal,
  [MODAL_TYPES.EREADER_LAUNCH]: EreaderLaunchModal,
  [MODAL_TYPES.EREADER_SUCCESS]: EreaderSuccessModal,
  [MODAL_TYPES.EREADER_EARLY_EXIT]: EreaderEarlyExitModal,
  [MODAL_TYPES.GAME_SUCCESS]: GameSuccessModal,
  [MODAL_TYPES.GAME_FAILURE]: GameFailureModal,
  [MODAL_TYPES.EARLY_EXIT]: GameEarlyExitModal,
  [MODAL_TYPES.NOTIFICATION]: NotificationModal,
  [MODAL_TYPES.END_OF_ALL_QUESTS_AND_CHALLENGES]: EndOfAllQuestsAndChallengesModal,
  [MODAL_TYPES.GAME_SELECTOR]: GameSelectorModal,
  [MODAL_TYPES.FLUENCY_LAUNCH]: FluencyLaunchModal,
  [MODAL_TYPES.CHALLENGE_QUEST_SUCCESS]: ChallengeQuestSuccessModal,
  [MODAL_TYPES.CHALLENGE_QUEST]: ChallengeQuestModal,
  [MODAL_TYPES.END_OF_QUESTS]: EndOfQuestsModal,
  [MODAL_TYPES.RE_PLACEMENT]: RePlacementModal,
  [MODAL_TYPES.LEVEL_SUMMARY]: LevelSummaryModal,
  [MODAL_TYPES.QUEST_COMPLETE]: QuestCompleteModal,
};

const WorkshopModalSystem = () => {
  const isModalOpen = useSelector(getIsModalOpen);
  const modalConfig = useSelector(getOpenModalConfig);
  const modalType = useSelector(getOpenModalType);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [modal, setCurrentModal] = useState({});

  const dispatch = useDispatch();
  const ModalComponent = MODAL_COMPONENTS[modal.type];
  const withBackground = useSelector(state => state.appSessionReducer.hasUserInteracted) || !!getDevDirectGameId();

  let closeTimeout = null;

  const closeModalHandler = ({ withFade = false } = {}) => {
    if (closeTimeout !== null) {
      clearTimeout(closeTimeout);
      closeTimeout = null;
    }
    const close = () => {
      setCurrentModal({});
      setShouldFadeOut(false);
      dispatch(closeModal(modal.type));
    };

    if (withFade) {
      setShouldFadeOut(true);
      closeTimeout = setTimeout(() => {
        close();
        closeTimeout = null;
      }, 2000);
    } else {
      close();
    }
  };

  // this is done to have an extra tick when switching modals
  // so the animation component can mount again and trigger intro anim.
  useEffect(() => {
    setCurrentModal({ type: modalType, config: modalConfig });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalType]);

  if (!isModalOpen || ModalComponent === undefined) {
    return null;
  }

  const { config } = modal;

  return (
    <AnimatedModal
      shouldFadeOut={shouldFadeOut}
      withBackground={withBackground}
      type={modal.type}
    >
      <ModalComponent
        closeModal={closeModalHandler}
        config={config}
      />
    </AnimatedModal>
  );
};

export default WorkshopModalSystem;
